import React, { useState } from 'react';
import TimePeriodSelector from './TimePeriodSelector';
import { sensorService } from '../services/sensorService';

interface HistoricalReadingsModalProps {
  sensorId: number;
  sensorName: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const HistoricalReadingsModal: React.FC<HistoricalReadingsModalProps> = ({
  sensorId,
  sensorName,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [timePeriod, setTimePeriod] = useState('24h');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  if (!isOpen) return null;

  const handleFetchHistorical = async () => {
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const result = await sensorService.fetchHistoricalReadings(sensorId, timePeriod);

      setSuccessMessage(
        `Successfully retrieved ${result.messages_retrieved} messages and imported ${result.readings_imported} new readings.`
      );

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch historical readings');
    } finally {
      setLoading(false);
    }
  };

  const handlePurgeReadings = async () => {
    if (!window.confirm(
      `Are you sure you want to delete readings from the last ${timePeriod}? This action cannot be undone.`
    )) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const result = await sensorService.purgeReadings(sensorId, timePeriod);

      setSuccessMessage(
        `Successfully deleted ${result.readings_deleted} readings from the last ${timePeriod}.`
      );

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to purge readings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Manage Historical Readings
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <span className="text-2xl">&times;</span>
            </button>
          </div>
          <p className="mt-1 text-sm text-gray-600">
            Sensor: <span className="font-medium">{sensorName}</span>
          </p>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Time Period Selector */}
          <TimePeriodSelector
            value={timePeriod}
            onChange={setTimePeriod}
            label="Select Time Period"
          />

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
              {successMessage}
            </div>
          )}

          {/* Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">What does this do?</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>
                <strong>Fetch Historical:</strong> Retrieves past readings stored in TTI and imports them into your dashboard
              </li>
              <li>
                <strong>Purge Readings:</strong> Permanently deletes readings from your local database for the selected time period
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleFetchHistorical}
              disabled={loading}
              className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </>
              ) : (
                <>
                  📥 Fetch Historical
                </>
              )}
            </button>

            <button
              onClick={handlePurgeReadings}
              disabled={loading}
              className="flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : '🗑️ Purge Readings'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default HistoricalReadingsModal;

